/* -------------------------------------------------------

    Cookies banner
    GTM - Google consentment v2
    Google analytics

    -----------
    Workflow
    -----------

    START
    │
    ▼
    Check localStorage
    │
    ├─ Valid consent → apply + hide banner
    │
    └─ No consent → set denied + show banner
            │
            ▼
    Inject GTM script + noscript
            │
            ▼
    Wait for user action
            │
            ├─ Accept all → update all granted
            ├─ Reject all → update all denied
            ├─ Customize → open manager
            └─ Save → update each property
            │
            ▼
    Update localStorage
            │
            ▼
    Hide manager + hide banner
            │
            ▼
    User may reopen manager later

   -----------------------------------------------------*/

   
export const registerCookiesSystem = () => {

    /* -------------------------------------------------------
        Settings
       ------------------------------------------------------- */

    // - Settings and constants
    const settings = {
    
        gtmId: document.body.dataset.gtm ?? '',
    
        localStorageName: 'cookies_consent',
    
        classes : {
            bannerActive : 'cookies-banner-active',
            managerActive: 'cookies-manager-active'
        },
    
        gtagConsentProperties: [
            'ad_storage', 
            'analytics_storage', 
            'ad_user_data', 
            'ad_personalization'
        ],
    
        statuses: {
            'allowed': 'granted',
            'denied' : 'denied'
        }
    
    };

    /* -------------------------------------------------------
        GTM events
        ------------------------------------------------------ */

    const updateGtagConsentProperty = (property, status) => {
    
        gtag('consent', 'update', {
            [property]: status
        });
    
        settings.gtagConsent[property] = status;
    
        setLocalStorage(settings.gtagConsent);
    
    }
    
    const constuctGtagProperties = status => {
    
        let statuses = {};

        for (let prop of settings.gtagConsentProperties) {

            statuses[prop] = status;

        }
    
        return statuses;
    
    };
    
    const updateGtagConsentAllProperties = status => {
    
        const gtagConsent = constuctGtagProperties(status);
    
        gtag('consent', 'update', gtagConsent);

        settings.gtagConsent = gtagConsent;
    
        setLocalStorage(gtagConsent);
    
    }

    /* -------------------------------------------------------
        Constants and variables
        -----------------------------------------------------*/

    // - DOM elements
    const DOMElements = {
     
        banner                  : document.querySelector('#cookies_banner'),
        manager                 : document.querySelector('#cookies_manager'),
        actionButtons           : document.querySelectorAll('[data-cookies-action]'),
        openManagerButtons      : document.querySelectorAll('[open-cookies-manager]'),
        
    };

    DOMElements.buttonsWrapper = DOMElements.manager ? DOMElements.manager.querySelectorAll('.toggle-group') : [];
    DOMElements.toggleButtons  = DOMElements.manager ? DOMElements.manager.querySelectorAll('.accordion header') : [];
    DOMElements.switchButtons  = DOMElements.manager ? DOMElements.manager.querySelectorAll('.--cm-button') : [];

    // - Default GTAG consent
    settings.gtagConsent = constuctGtagProperties(settings.statuses.denied);
    
    /* -------------------------------------------------------
        Script injection
        -----------------------------------------------------*/

    // - Define dataLayer and the gtag function. Must be in higher level (scope)
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}

    const initScript = () => {
    
        const user_cookies_consent = JSON.parse(localStorage.getItem(settings.localStorageName));
    
        // - If the the user has a storage cookies consent & the time hasn't expired, get the previous choices of the user
        if (user_cookies_consent && new Date() < user_cookies_consent.expiration_date) {
    
            settings.gtagConsent = user_cookies_consent.gtag;
    
            showBanner(false);
    
        } else {
    
            // - Default GTAG consent
            settings.gtagConsent = constuctGtagProperties(settings.statuses.denied);
    
            showBanner(true);
    
        }
    
        // - Set default consent to 'denied' as a placeholder
        gtag('consent', 'default', settings.gtagConsent);
    
        const cdnScript = document.createElement('script');
    
        cdnScript.innerHTML = `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer', '${settings.gtmId}'); 
        `;
    
        const htmlHead = document.querySelector('head');
    
        // Add the GTM Script
        htmlHead.appendChild(document.createComment('Google Tag Manager'));
        htmlHead.appendChild(cdnScript);
        htmlHead.appendChild(document.createComment('End Google Tag Manager'));
    
        // - Add the GTM No Script
        const cdnNoScript = document.createElement('noscript');
        const cdnNoScriptIframe = document.createElement('iframe');
    
        cdnNoScriptIframe.src = `https://www.googletagmanager.com/ns.html?id=${settings.gtmId}`;
        cdnNoScriptIframe.setAttribute('height', '0');
        cdnNoScriptIframe.setAttribute('width', '0');
        cdnNoScriptIframe.setAttribute('style', 'display:none;visibility:hidden');
    
        cdnNoScript.append(cdnNoScriptIframe);
    
        document.body.prepend(document.createComment("Google Tag Manager (noscript)"));
        document.body.prepend(cdnNoScript);
        document.body.prepend(document.createComment("End Google Tag Manager (noscript)"));
    
    }

    /* -------------------------------------------------------
        Modal visibility
        -----------------------------------------------------*/

    const showBanner = active => {
    
        if (active) {
    
            // - Show the cookies banner
            document.body.classList.add(settings.classes.bannerActive);
            // DOMElements.banner.style.transform = 'translateY(0)';
            // DOMElements.banner.classList.add('active');
    
         } else {
    
            // - Hide the cookies banner
            document.body.classList.remove(settings.classes.bannerActive);
            // DOMElements.banner.style.transform = 'translateY(130%)';
    
        }
    
    };
    
    const showManager = active => {
    
        if (active) {
    
            // - Show the cookies manager
            document.body.classList.add(settings.classes.managerActive);
            document.documentElement.classList.add(settings.classes.managerActive);
            DOMElements.manager.style.opacity = 1;
    
         } else {
    
            // - Hide the cookies manager
            document.body.classList.remove(settings.classes.managerActive);
            document.documentElement.classList.remove(settings.classes.managerActive);
            DOMElements.manager.style.opacity = 0;
    
        }
    
    };
    
    /* -------------------------------------------------------
        Localstorage
        -----------------------------------------------------*/

    const setLocalStorage = gtag_consent_data => {
    
        const consent_storage = {
            "gtag": gtag_consent_data,
            "expiration_date": new Date().setMonth(new Date().getMonth() + 12)
        };
    
        localStorage.removeItem(settings.localStorageName);
        localStorage.setItem(settings.localStorageName, JSON.stringify(consent_storage));
    
    };
    
    /* -------------------------------------------------------
        Events
        -----------------------------------------------------*/

    const registerCookiesEvents = () => {
    
        // - Cookies Manager switches
        DOMElements.switchButtons.forEach(button => {
    
            button.addEventListener('click', () => {
    
                button.parentElement.querySelector('.active').classList.remove('active');
                button.classList.toggle('active');
    
            });
    
        });
    
        // - Accordeons
        DOMElements.toggleButtons.forEach(header => {

            header.addEventListener('click', () => {

                header.closest('.accordion').classList.toggle('active');

            });

            header.querySelector('.toggle-group')?.addEventListener('click', e => {

                e.stopPropagation();

            });

        });
    
        // - Triggers
        DOMElements.actionButtons?.forEach(trigger => {
    
            trigger.addEventListener('click', () => {
    
                const action = trigger.dataset.cookiesAction;
    
                switch (action) {
    
                    // - Saving from the cookies management screen
                    case 'save' :
    
                        document.querySelectorAll('[data-cookies-type]').forEach(parent => {
    
                            const consent_type = parent.dataset.cookiesType;
                            const status = parent.querySelector('.active').dataset.status == 'accept' ? settings.statuses.allowed : settings.statuses.denied;
    
                            if (settings.gtagConsentProperties.includes(consent_type)) {
    
                                updateGtagConsentProperty(consent_type, status);
    
                            }
    
                        });
    
                        setLocalStorage(settings.gtagConsent);
    
                        console.log(settings.gtagConsent);
    
                        break;
    
                    // - Accept all cookies
                    case 'all':
    
                        updateGtagConsentAllProperties(settings.statuses.allowed);
    
                        break;
    
                    // - Refuse all cookies
                    case 'deny':
                    case 'required':
    
                        updateGtagConsentAllProperties(settings.statuses.denied);
    
                        break;
    
                    // - Open the cookies management screen
                    case 'custom':
    
                        showManager(true);
    
                        break;
    
                }
    
                if (action !== 'custom') {
    
                    showManager(false);
    
                }
    
                showBanner(false);
    
            });
    
        });
    
        // - Update previous choices
        DOMElements.openManagerButtons?.forEach(trigger => {
    
            trigger.addEventListener('click', () => {
        
                const cookies_values = JSON.parse(localStorage.getItem(settings.localStorageName));
        
                DOMElements.buttonsWrapper?.forEach(wrapper => {
        
                    const current_cookies_type = wrapper.closest('[data-cookies-type]')?.dataset.cookiesType;
        
                    if (current_cookies_type) {
        
                        wrapper.querySelector('.active').classList.remove('active');
        
                        if (cookies_values.gtag[`${current_cookies_type}`] == settings.statuses.denied) {
        
                            wrapper.querySelector('[data-status="decline"]')?.classList.add('active');
        
                        } else {
        
                            wrapper.querySelector('[data-status="accept"]')?.classList.add('active');
        
                        }
        
                    }
        
                });
    
                showManager(true);
    
            });
    
        });
    
    };

    /* -------------------------------------------------------
        Initialization
        -----------------------------------------------------*/

    initScript();

    registerCookiesEvents();

};